import React, { useMemo } from "react";
import { useAuth } from "../../hooks/useAuth";
import { Card, Table, Tag, Typography, Row, Col, Badge } from "antd";
import { RocketOutlined, LockOutlined, UnlockOutlined } from "@ant-design/icons";
import { useIntl } from "react-intl";
import { FeatureTypeEnum } from "../../enums/FeatureTypeEnum";
import { FeatureStatusEnum } from "../../enums/FeatureStatusEnum";
import { PlanLevelEnum } from "../../enums/PlanLevelEnum";
import { IOrganisationFeatures } from "../../services/nestjs-base-api/types";
import {useFeatureValue} from "../../hooks/useFeatureValue";
import {useMaxEvents, useMaxGalleriesPerEvent, useMaxPhotosPerGallery} from "../../hooks/useSystemSetting";

const { Title, Text, Paragraph } = Typography;

interface IProps {
}

interface IFeatureInfo {
  name: string;
  description: string;
  type: FeatureTypeEnum;
  status: FeatureStatusEnum;
  value: string;
  unlockLevel: PlanLevelEnum;
  isUnlocked: boolean;
}

const OrganisationFeatures: React.FunctionComponent<IProps> = () => {
  const intl = useIntl();
  const { user } = useAuth();

  const [maxEvents, isMaxEventsFeatureActive] = useFeatureValue(FeatureTypeEnum.MaxEvents, useMaxEvents("5"));
  const [maxGalleriesPerEvent, isMaxGalleriesFeatureActive] = useFeatureValue(FeatureTypeEnum.MaxGalleriesPerEvent, useMaxGalleriesPerEvent("1"));
  const [maxPhotosPerGallery, isMaxPhotosFeatureActive] = useFeatureValue(FeatureTypeEnum.MaxPhotosPerGallery, useMaxPhotosPerGallery("1000"));

  const getPlanLabel = (plan: PlanLevelEnum): string => {
    switch (plan) {
      case PlanLevelEnum.Plan1:
        return intl.formatMessage({ id: 'organisation.plan.basic' }) || 'Basic';
      case PlanLevelEnum.Plan2:
        return intl.formatMessage({ id: 'organisation.plan.professional' }) || 'Professional';
      case PlanLevelEnum.Plan3:
        return intl.formatMessage({ id: 'organisation.plan.enterprise' }) || 'Enterprise';
      case PlanLevelEnum.Plan4:
        return intl.formatMessage({ id: 'organisation.plan.custom' }) || 'Custom';
      default:
        return plan;
    }
  };

  const getFeatureDescription = (type: FeatureTypeEnum): string => {
    switch (type) {
      case FeatureTypeEnum.MaxEvents:
        return intl.formatMessage({ id: 'features.maxEvents.description' }) || 'Maximum number of events you can create';
      case FeatureTypeEnum.MaxGalleriesPerEvent:
        return intl.formatMessage({ id: 'features.maxGalleriesPerEvent.description' }) || 'Maximum number of galleries per event';
      case FeatureTypeEnum.MaxPhotosPerGallery:
        return intl.formatMessage({ id: 'features.maxPhotosPerGallery.description' }) || 'Maximum number of photos per gallery';
      default:
        return '';
    }
  };

  const getFeatureDisplayName = (type: FeatureTypeEnum): string => {
    switch (type) {
      case FeatureTypeEnum.MaxEvents:
        return intl.formatMessage({ id: 'features.maxEvents.name' }) || 'Maximum Events';
      case FeatureTypeEnum.MaxGalleriesPerEvent:
        return intl.formatMessage({ id: 'features.maxGalleriesPerEvent.name' }) || 'Galleries Per Event';
      case FeatureTypeEnum.MaxPhotosPerGallery:
        return intl.formatMessage({ id: 'features.maxPhotosPerGallery.name' }) || 'Photos Per Gallery';
      default:
        return type;
    }
  };

  const features = useMemo(() => {
    const defaultFeatures: IFeatureInfo[] = [
      {
        name: getFeatureDisplayName(FeatureTypeEnum.MaxEvents),
        description: getFeatureDescription(FeatureTypeEnum.MaxEvents),
        type: FeatureTypeEnum.MaxEvents,
        status: FeatureStatusEnum.Active,
        value: maxEvents,
        unlockLevel: PlanLevelEnum.Plan2,
        isUnlocked: user.organisation.plan !== PlanLevelEnum.Plan1
      },
      {
        name: getFeatureDisplayName(FeatureTypeEnum.MaxGalleriesPerEvent),
        description: getFeatureDescription(FeatureTypeEnum.MaxGalleriesPerEvent),
        type: FeatureTypeEnum.MaxGalleriesPerEvent,
        status: FeatureStatusEnum.Active,
        value: maxGalleriesPerEvent,
        unlockLevel: PlanLevelEnum.Plan2,
        isUnlocked: user.organisation.plan !== PlanLevelEnum.Plan1
      },
      {
        name: getFeatureDisplayName(FeatureTypeEnum.MaxPhotosPerGallery),
        description: getFeatureDescription(FeatureTypeEnum.MaxPhotosPerGallery),
        type: FeatureTypeEnum.MaxPhotosPerGallery,
        status: FeatureStatusEnum.Active,
        value: maxPhotosPerGallery,
        unlockLevel: PlanLevelEnum.Plan2,
        isUnlocked: user.organisation.plan !== PlanLevelEnum.Plan1
      }
    ];

    if (user.organisation?.organisationFeaturesDto) {
      try {
        const organisationFeatures: IOrganisationFeatures = JSON.parse(user.organisation.organisationFeaturesDto);
        
        return defaultFeatures.map(defaultFeature => {
          const featureFromDto = organisationFeatures.features.find(
            (feature) => feature.name === defaultFeature.type
          );

          if (featureFromDto) {
            return {
              ...defaultFeature,
              status: featureFromDto.specification.status,
              value: featureFromDto.specification.value,
              unlockLevel: featureFromDto.specification.unlockLevel,
              isUnlocked: user.organisation.plan >= featureFromDto.specification.unlockLevel
            };
          }
          
          return defaultFeature;
        });
      } catch (error) {
        console.error('Error parsing organisation features:', error);
      }
    }
    
    return defaultFeatures;
  }, [user.organisation, intl, getFeatureDescription, getFeatureDisplayName, maxEvents, maxGalleriesPerEvent, maxPhotosPerGallery]);

  const columns = [
    {
      title: intl.formatMessage({ id: 'features.table.feature' }) || 'Feature',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: IFeatureInfo) => (
        <div>
          <Text strong>{text}</Text>
          <br />
          <Text type="secondary">{record.description}</Text>
        </div>
      ),
    },
    {
      title: intl.formatMessage({ id: 'features.table.value' }) || 'Value',
      dataIndex: 'value',
      key: 'value',
      render: (text: string, record: IFeatureInfo) => {
        return record.status === FeatureStatusEnum.Active ? text : '-';
      },
    },
    {
      title: intl.formatMessage({ id: 'features.table.status' }) || 'Status',
      key: 'status',
      dataIndex: 'status',
      render: (status: FeatureStatusEnum, record: IFeatureInfo) => {
        const color = record.isUnlocked ? 'green' : 'red';
        const icon = record.isUnlocked ? <UnlockOutlined /> : <LockOutlined />;
        const text = record.isUnlocked
          ? intl.formatMessage({ id: 'features.status.unlocked' }) || 'Unlocked'
          : intl.formatMessage({ id: 'features.status.locked' }) || 'Locked';
        
        return (
          <Tag color={color} icon={icon}>
            {text}
          </Tag>
        );
      },
    },
    {
      title: intl.formatMessage({ id: 'features.table.availableIn' }) || 'Available In',
      key: 'unlockLevel',
      dataIndex: 'unlockLevel',
      render: (unlockLevel: PlanLevelEnum) => getPlanLabel(unlockLevel),
    },
  ];

  return (
    <div>
      <Card title={<><RocketOutlined /> {intl.formatMessage({ id: 'organisation.tabs.features' }) || 'Features'}</>}>
        <Paragraph>
          {intl.formatMessage({ id: 'features.description' }) || 
            'Your current plan includes the following features. Upgrade your plan to unlock more capabilities.'}
        </Paragraph>
        
        <Row style={{ marginBottom: 16 }}>
          <Col>
            <Badge 
              color="blue" 
              text={
                <Text strong>
                  {intl.formatMessage(
                    { id: 'features.currentPlan' },
                    { plan: getPlanLabel(user.organisation.plan) }
                  ) || `Current Plan: ${getPlanLabel(user.organisation.plan)}`}
                </Text>
              } 
            />
          </Col>
        </Row>
        
        <Table 
          columns={columns} 
          dataSource={features} 
          rowKey="name"
          pagination={false}
        />
      </Card>
    </div>
  );
};

export default OrganisationFeatures;
